import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { useSelector } from '@state/store'
import moment from 'moment'
import { PreparationLotDto, PreparationStatus, QualityControlStatus } from '@services/dtos'
import { getPreparationLots, getPreparationLotCurrentActivities, setPreparationLotFilters } from '@state/reducers/preparationLotReducer'
import { Box, Chip, Container, Dialog, DialogContent, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InfoIcon from '@mui/icons-material/Info'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import ListAltIcon from '@mui/icons-material/ListAlt'
import FlakyIcon from '@mui/icons-material/Flaky'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AlarmIcon from '@mui/icons-material/Alarm'
import { PreparationLotDetailsDialog } from '@components/dialogs/PreparationLotDetailsDialog'
import { PreparationLotStepsDialog } from '@components/dialogs/PreparationLotStepsDialog'
import { CERCLAGE_COLORS } from '@utils/constants'
import { ChipPreparationStatus } from '@components/ChipPreparationStatus'
import { ChipQualityControlStatus } from '@components/ChipQualityControlStatus'
import { QualityControlsDialog } from '@components/dialogs/QualityControlsDialog'
import generatePDF, { Margin } from 'react-to-pdf'
import PreparationLotDymoLabel from '@components/labels/PreparationLotDymoLabel'
import { isNil } from 'lodash'
import { usePreparationLotSSE } from '@hooks/usePreparationLotSSE'
import { PreparationLotStepTimerDto } from '@services/dtos'
import { useTimerCountdown, formatCountdown } from '@hooks/useTimerCountdown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { UpTransition } from '@components/animations'
import WasteEntryFromSourceForm from '@components/forms/WasteEntryFromSourceForm'
import { ChipWastedStatus } from '../ChipWastedStatus'

function StepTimerDisplay({ timer }: { timer: PreparationLotStepTimerDto }) {
  const remaining = useTimerCountdown(timer.startedAt, timer.durationSeconds)
  if (remaining === null) {
    return null
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <AlarmIcon sx={{ fontSize: 14, color: remaining <= 0 ? 'warning.main' : 'primary.main' }} />
      <Typography variant="caption" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums', color: remaining <= 0 ? 'warning.main' : 'primary.main' }}>
        {remaining <= 0 ? 'Terminé' : formatCountdown(remaining)}
      </Typography>
    </Box>
  )
}

interface Props {
  containerClassName?: string
  className?: string
  hoveredElutionId?: string | null
}

type ActionMenuProps = {
  row: PreparationLotDto
  onDetails: () => void
  onSteps: () => void
  onQualityControls: () => void
  onWaste: () => void
}

const ActionMenu = ({ row, onDetails, onSteps, onQualityControls, onWaste }: ActionMenuProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const isDraft = row.status === PreparationStatus.DRAFT
  const hasSteps = (row.compound.productionSteps?.length ?? 0) > 0

  const close = () => setAnchorEl(null)

  const stepsLabel = isDraft
    ? (hasSteps ? 'Gérer les étapes de production' : 'Marquer comme prêt')
    : 'Consulter les étapes de production'

  return (
    <>
      <PreparationLotDymoLabel preparationLot={row} ref={ref} />
      <Tooltip title="Voir les détails">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDetails() }}>
          <InfoIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          disabled={!isDraft && !hasSteps}
          onClick={() => { close(); onSteps() }}
        >
          <ListItemIcon><ListAltIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'primary' }}>{stepsLabel}</ListItemText>
        </MenuItem>

        {row.qualityControlStatus !== QualityControlStatus.NONE && (
          <MenuItem onClick={() => { close(); onQualityControls() }}>
            <ListItemIcon><FlakyIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'primary' }}>Gérer les contrôles qualité</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => {
          close()
          generatePDF(ref, {
            method: 'open',
            filename: `etiquette-preparation-${row.no}.pdf`,
            page: { margin: Margin.NONE, format: [89, 36], orientation: 'landscape' },
          })
        }}>
          <ListItemIcon><LocalOfferOutlinedIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'primary' }}>Étiquette Dymo</ListItemText>
        </MenuItem>

        {!row.wasteEntry && (
          <MenuItem onClick={() => { close(); onWaste() }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'primary' }}>Mise en déchet</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

export default function PreparationLotsTable({ containerClassName, className, hoveredElutionId }: Props) {
  const { preparationLots, totalCount, loading } = useSelector(({ preparationLot }) => ({
    preparationLots: preparationLot.items.datas,
    totalCount: preparationLot.items.totalCount,
    loading: preparationLot.loading,
  }))
  const dispatch = useAppDispatch()
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [stepsDialogOpen, setStepsDialogOpen] = useState(false)
  const [qcDialogOpen, setQcDialogOpen] = useState(false)
  const [wasteDialogOpen, setWasteDialogOpen] = useState(false)
  const preparationLotsRef = useRef(preparationLots)
  usePreparationLotSSE()

  useEffect(() => {
    preparationLotsRef.current = preparationLots
  }, [preparationLots])

  useEffect(() => {
    dispatch(getPreparationLots())
    const interval = setInterval(() => {
      const ids = preparationLotsRef.current.map(({ id }) => id)
      if (ids.length > 0) {
        dispatch(getPreparationLotCurrentActivities(ids))
      }
    }, 30_000)
    return () => {
      clearInterval(interval)
      dispatch(setPreparationLotFilters({ page: 0 }))
    }
  }, [])

  const selectedPreparation = preparationLots.find((preparationLot) => preparationLot.id === selectedItemId) ?? null

  const columns: GridColumns<PreparationLotDto> = useMemo(
    () => [
      {
        field: '__actions__',
        headerName: 'Actions',
        width: 75,
        sortable: false,
        renderCell: (params: any) => {
          const row = params.row as PreparationLotDto
          return (
            <ActionMenu
              row={row}
              onDetails={() => { setSelectedItemId(row.id); setDetailsDialogOpen(true) }}
              onSteps={() => { setSelectedItemId(row.id); setStepsDialogOpen(true) }}
              onQualityControls={() => { setSelectedItemId(row.id); setQcDialogOpen(true) }}
              onWaste={() => { setSelectedItemId(row.id); setWasteDialogOpen(true) }}
            />
          )
        },
      },
      { field: 'no', headerName: 'N°', width: 50 },
      {
        field: 'cerclageColor', headerName: '', width: 25,
        renderCell: (params) => {
          const cerclageColor = CERCLAGE_COLORS.find(c => c.color === params.row.cerclageColor)
          return cerclageColor ? (
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: cerclageColor.hex, border: '1px solid rgba(0,0,0,0.15)' }} />
          ) : null
        },
      },
      {
        field: 'status',
        headerName: 'Statut',
        width: 150,
        renderCell: (params) => {
          const preparation = params.row
          if (preparation.wasteEntry) {
            return (<ChipWastedStatus wasteEntry={preparation.wasteEntry} />)
          }
          const nextStepIndex = (preparation.currentStepIndex ?? -1) + 1
          const activeTimer = preparation.activeStepTimer?.stepIndex === nextStepIndex ? preparation.activeStepTimer : undefined
          return (
            <Box display="flex" flexDirection="column" gap={0.25} sx={{ mt: activeTimer ? 0.5 : 0 }}>
              <ChipPreparationStatus
                status={preparation.status}
                steps={preparation.compound.productionSteps ?? []}
                currentStepIndex={preparation.currentStepIndex}
              />
              {activeTimer && (
                <StepTimerDisplay timer={activeTimer} />
              )}
            </Box>
          )
        },
      },
      {
        field: 'preparedAt',
        headerName: 'Date',
        width: 145,
        renderCell: (params) => {
          const row = params.row as PreparationLotDto
          const formatted = moment(row.preparedAt).format('DD/MM/YYYY HH:mm')
          const maxAge = row.compound.preparationMaxAgeMinutes
          const isOverAge = !isNil(maxAge) && moment().diff(moment(row.preparedAt), 'minutes') > maxAge
          return (
            <div className="flex items-center gap-1">
              {isOverAge && (
                <Tooltip title={`Âge max dépassé (limite : ${maxAge} min)`} arrow>
                  <WarningAmberIcon fontSize="small" color="warning" />
                </Tooltip>
              )}
              <span>{formatted}</span>
            </div>
          )
        },
      },
      {
        field: 'compoundName',
        headerName: 'MRP',
        width: 135,
        valueGetter: (params) => params.row.compound.name ?? null,
      },
      {
        field: 'qualityControlStatus',
        headerName: 'CQ',
        width: 75,
        sortable: false,
        renderCell: (params) => <ChipQualityControlStatus status={params.row.qualityControlStatus} qualityControlDone={params.row.qualityControlDone} qualityControlTotal={params.row.qualityControlTotal} />,
      },
      {
        field: 'activityAtRefMbq',
        headerName: 'Activité (MBq)',
        width: 135,
        renderHeader: () => (
          <div className="flex items-center gap-1">
            <Tooltip title="Recalculée toutes les 30s par décroissance radioactive" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }} />
            </Tooltip>
            <Typography variant="body2" fontWeight={500}>Activité (MBq)</Typography>
          </div>
        ),
        renderCell: (params) => {
          const row = params.row as PreparationLotDto
          if (row.activityAtTimeMbq === null) {
            return <span>{row.activityAtRefMbq.toFixed(2)}</span>
          }
          return (
            <div className="flex items-center gap-1">
              <span className="font-medium">{row.activityAtTimeMbq.toFixed(2)}</span>
              <span>/ {row.activityAtRefMbq.toFixed(2)}</span>
            </div>
          )
        },
      },
      {
        field: 'totalVolumeMl',
        headerName: 'Volume (mL)',
        width: 105,
        valueGetter: (params) => !isNil(params.row.totalVolumeMl) ? `${params.row.totalVolumeMl.toFixed(2)} / ${params.row.totalVolumeMl.toFixed(2)}` : '—',
      },
    ],
    [],
  )

  return (
    <Container maxWidth={false} style={{ flex: 1, minHeight: 0, padding: '0', display: 'flex', flexDirection: 'column' }} className={containerClassName}>
      <DataGridPro
        density="standard"
        pagination
        pageSize={20}
        rowsPerPageOptions={[20]}
        paginationMode="server"
        style={{ height: '100%', width: '100%', backgroundColor: 'white', borderRadius: '15px 15px 25px 25px' }}
        className={className}
        onPageChange={(page) => { dispatch(setPreparationLotFilters({ page })) }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#f8f8f8' },
          '& .row-elution-highlighted': { backgroundColor: '#E2E6F2 !important' },
        }}
        getRowClassName={(params) =>
          hoveredElutionId && params.row.sourceElutions?.some((e) => e.id === hoveredElutionId)
            ? 'row-elution-highlighted'
            : ''
        }
        columns={columns}
        rows={preparationLots}
        rowCount={totalCount}
        loading={loading}
        disableSelectionOnClick
        disableMultipleSelection
      />

      <Dialog open={wasteDialogOpen} onClose={() => setWasteDialogOpen(false)} TransitionComponent={UpTransition} maxWidth="sm" fullWidth>
        <DialogContent>
          {wasteDialogOpen && selectedPreparation && (
            <WasteEntryFromSourceForm
              preparation={selectedPreparation}
              onClose={() => { setWasteDialogOpen(false); setSelectedItemId(null) }}
            />
          )}
        </DialogContent>
      </Dialog>

      <PreparationLotDetailsDialog
        open={detailsDialogOpen}
        preparationLot={selectedPreparation}
        onClose={() => { setDetailsDialogOpen(false); setSelectedItemId(null) }}
      />

      <PreparationLotStepsDialog
        open={stepsDialogOpen}
        preparationLot={selectedPreparation}
        readOnly={selectedPreparation?.status !== PreparationStatus.DRAFT}
        onClose={() => { setStepsDialogOpen(false); setSelectedItemId(null) }}
      />

      {selectedPreparation && (
        <QualityControlsDialog
          open={qcDialogOpen}
          onClose={() => { setQcDialogOpen(false); setSelectedItemId(null) }}
          entityType="preparationLot"
          entity={selectedPreparation}
        />
      )}
    </Container>
  )
}
